/**
 * Sign in with Apple / Google (Stage 9). On the phone, the native sheet (the system's Apple sheet, Google's account
 * picker; @capgo/capacitor-social-login) hands back an ID token, and Supabase signs in with it (signInWithIdToken) - no
 * browser hop. On the web, Supabase's own redirect (signInWithOAuth) to Apple / Google and back to this page.
 *
 * A nonce makes the token single-use: the provider is given its SHA-256 and Supabase the raw value, which it hashes and
 * compares with the token's claim.
 *
 * Apple tells an app the person's name only on the very first sign-in (never in the token), so it is saved to the
 * account then (display_name); with no name at all the Display name step asks for one (NeedsDisplayName).
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '../../supabaseClient';
import { GOOGLE_IOS_CLIENT_ID, GOOGLE_WEB_CLIENT_ID } from '../../config/socialSignIn';

const NATIVE = Capacitor.isNativePlatform();

async function sha256Hex(text) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function newNonce() {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

let initialized = null;
async function plugin() {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  if (!initialized) {
    initialized = SocialLogin.initialize({
      apple: { redirectUrl: '' }, // iOS: the system sheet, no redirect
      google: {
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
        webClientId: GOOGLE_WEB_CLIENT_ID,
        mode: 'online',
      },
    });
  }
  await initialized;
  return SocialLogin;
}

/** A cancelled sheet is not an error: the caller just stays where it was. */
export function isCancelled(err) {
  return /cancel/i.test(String(err?.message || err?.code || err || ''));
}

/**
 * Signs in with 'apple' or 'google'. Resolves the Supabase session (native), or nothing when the page is being
 * redirected (web). Throws on failure; a cancelled sheet throws an error isCancelled() recognises.
 */
export async function signInWithProvider(provider) {
  if (!NATIVE) {
    // Back to this very page: the sign-in pop-up's pending action (Join, an invite) finishes as the session arrives.
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.href },
    });
    if (error) throw error;
    return null;
  }
  const SocialLogin = await plugin();
  const nonce = newNonce();
  const hashed = await sha256Hex(nonce);
  const { result } =
    provider === 'apple'
      ? await SocialLogin.login({
          provider: 'apple',
          options: { scopes: ['name', 'email'], nonce: hashed },
        })
      : await SocialLogin.login({
          provider: 'google',
          options: { scopes: ['profile', 'email'], nonce: hashed },
        });
  if (!result?.idToken) throw new Error('Could not sign in.');
  const { data, error } = await supabase.auth.signInWithIdToken({
    provider,
    token: result.idToken,
    nonce,
  });
  if (error) throw error;
  // Apple's name, the one time it gives it: onto the account, unless it already has one.
  const given = result.profile?.givenName?.trim?.();
  const name = [given, result.profile?.familyName?.trim?.()].filter(Boolean).join(' ');
  if (provider === 'apple' && name && data?.user && !data.user.user_metadata?.display_name) {
    await supabase.auth.updateUser({ data: { display_name: name } });
    await supabase.from('profiles').update({ display_name: name }).eq('id', data.user.id);
  }
  return data?.session ?? null;
}
